'use client'

import * as React from 'react'

// Shared "reserve" basket for the marketplace section. Lives at the
// /marketplace layout level so the basket persists as the visitor moves
// between the library and individual clip pages, and survives a reload via
// localStorage. No checkout yet — this is the early-access intent signal.

const STORAGE_KEY = 'idukki:marketplace:reserved'

type ReserveCtx = {
  reserved: Set<string>
  has: (id: string) => boolean
  toggle: (id: string) => void
  clear: () => void
}

const ReserveContext = React.createContext<ReserveCtx | null>(null)

export function ReserveProvider({ children }: { children: React.ReactNode }) {
  // Start empty on both server and first client render (no hydration
  // mismatch); load any saved basket once mounted.
  const [reserved, setReserved] = React.useState<Set<string>>(() => new Set())

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const arr = JSON.parse(raw)
        if (Array.isArray(arr)) setReserved(new Set(arr.filter((x) => typeof x === 'string')))
      }
    } catch {
      /* ignore corrupt / unavailable storage */
    }
  }, [])

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...reserved]))
    } catch {
      /* ignore */
    }
  }, [reserved])

  const toggle = React.useCallback((id: string) => {
    setReserved((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const clear = React.useCallback(() => setReserved(new Set()), [])
  const has = React.useCallback((id: string) => reserved.has(id), [reserved])

  const value = React.useMemo<ReserveCtx>(
    () => ({ reserved, has, toggle, clear }),
    [reserved, has, toggle, clear],
  )

  return <ReserveContext.Provider value={value}>{children}</ReserveContext.Provider>
}

export function useReserve(): ReserveCtx {
  const ctx = React.useContext(ReserveContext)
  if (!ctx) throw new Error('useReserve must be used within a ReserveProvider')
  return ctx
}
